import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CourseStatusEnum } from '@edtech-ui/constants/course-status.enum';
import { AddTopic, AddTopicResponse } from '@edtech-ui/core/models/ai-course/add-topic';
import { AddUnit, AddUnitResponse } from '@edtech-ui/core/models/ai-course/add-unit';
import { CompleteCourseDataAI, CompleteSyllabusResponseAI, GetCoursesResponse } from '@edtech-ui/core/models/ai-course/ai-complete-syllabus';
import { SyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-generate-syllabus';
import { DeleteTopicResponse } from '@edtech-ui/core/models/ai-course/delete-topic-by-id';
import { DeleteUnitByIdResponse } from '@edtech-ui/core/models/ai-course/delete-unit-by-id';
import { UpdateCourseDataAI } from '@edtech-ui/core/models/ai-course/edit-course';
import { UpdateTopicById, UpdatetopicByIdResponse } from '@edtech-ui/core/models/ai-course/update-topic-by-id';
import { UpdateUnitByIdPayload, UpdateUnitByIdResponse } from '@edtech-ui/core/models/ai-course/update-unit-by-id';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, Subject, tap } from 'rxjs';
import { CourseResponse, Course } from '@edtech-ui/core/models/shared-resources/shared-resource.model';
import { PublishCourseResponse } from '@edtech-ui/core/models/ai-course/published-course-by-class-id';
import { CompleteSyllabusResponseManual, ManualCourse } from '@edtech-ui/core/models/manual-course/manual-couse-payload';

@Injectable({
	providedIn: 'root'
})
export class CourseManagementService {

	apiUrl = environment.courseApiUrl;
	apiUrl$ = environment.studentCourseApiUrl;

	saveSyllabusData = new BehaviorSubject<SyllabusResponseAI['Data'] | null>(null);
	reload$ = new Subject<void>;

	constructor(private http: HttpClient) { }

	postGenerateSyllabus(formData: FormData, classroomId: number | string): Observable<SyllabusResponseAI> {
		const params = new HttpParams().set('classroom_id', classroomId.toString());
		return this.http.post<SyllabusResponseAI>(`${this.apiUrl}/generateSyllabus`, formData, { params });
	}

	postCompleteSyllbus(payload: CompleteCourseDataAI, classroomId: number | string, courseStatus: CourseStatusEnum = CourseStatusEnum.Published): Observable<CompleteSyllabusResponseAI> {
		const params = new HttpParams().set('classroom_id', classroomId.toString()).set('course_status', courseStatus);
		return this.http.post<CompleteSyllabusResponseAI>(`${this.apiUrl}/completeCourse`, payload, { params });
	}

	getCourses(classroomId: number | string, page: number = 1, limit: number = 10, sort: 'created_at',
		order: 'asc' | 'desc' = 'desc', statusFilter: 'all' | 'Draft' | 'Published' = 'all'): Observable<GetCoursesResponse> {
		const params = new HttpParams()
			.set('classroom_id', classroomId.toString())
			.set('page', page.toString())
			.set('limit', limit.toString())
			.set('sort', sort)
			.set('order', order)
			.set('status_filter', statusFilter);
		return this.http.get<GetCoursesResponse>(`${this.apiUrl}/getCourses`, { params });
	}

	getCoursesByCourseId(courseId: string | number): Observable<CompleteSyllabusResponseAI> {
		return this.http.get<CompleteSyllabusResponseAI>(`${this.apiUrl}/getCourseByCourseId/${courseId}`);
	}

	deleteCourseById(id: number | string): Observable<any> {
		return this.http.delete(`${this.apiUrl}/deleteCourse/${id}`).pipe(
			tap(() => this.reload$.next())
		);
	}

	deleteAdminCourseById(id: number | string): Observable<any> {
		return this.http.delete(`${this.apiUrl$}/courses/${id}`).pipe(
			tap(() => this.reload$.next())
		);
	}

	postUpdateCourse(
		payload: UpdateCourseDataAI,
		courseId: string | number,
		thumbnailFile: File | null,
		documentFile: File | null
	) {
		const formData = new FormData();

		formData.append("request", JSON.stringify(payload));
		formData.append("course_thumbnail", thumbnailFile ?? "");
		formData.append("course_document", documentFile ?? "");

		return this.http.patch<SyllabusResponseAI>(
			`${this.apiUrl}/updateCourse/${courseId}`,
			formData
		);
	}


	deleteUnitById(courseId: string, unitId: string): Observable<DeleteUnitByIdResponse> {
		return this.http.delete<DeleteUnitByIdResponse>(`${this.apiUrl}/courses/${courseId}/units/${unitId}`).pipe(
			tap(() => this.reload$.next())
		);
	}

	postUpdateUnitById(courseId: string, unitId: string, payload: UpdateUnitByIdPayload): Observable<UpdateUnitByIdResponse> {
		return this.http.patch<UpdateUnitByIdResponse>(`${this.apiUrl}/courses/${courseId}/units/${unitId}`, payload);
	}

	postUpdateTopicById(courseId: string, unitId: string, topicId: string, topicNo: string, payload: UpdateTopicById): Observable<UpdatetopicByIdResponse> {
		return this.http.patch<UpdatetopicByIdResponse>(`${this.apiUrl}/courses/${courseId}/units/${unitId}/topics/${topicId}`, payload);
	}

	postAddUnit(courseId: string, payload: AddUnit): Observable<AddUnitResponse> {
		return this.http.post<AddUnitResponse>(`${this.apiUrl}/courses/${courseId}/units`, payload);
	}

	postAddtopic(courseId: string, unitId: string, payload: AddTopic): Observable<AddTopicResponse> {
		return this.http.post<AddTopicResponse>(`${this.apiUrl}/courses/${courseId}/units/${unitId}/topics`, payload);
	}

	deleteTopicById(courseId: string, unitId: string, topicId: string): Observable<DeleteTopicResponse> {
		return this.http.delete<DeleteTopicResponse>(`${this.apiUrl}/courses/${courseId}/units/${unitId}/topics/${topicId}?topic_id=${topicId}`).pipe(
			tap(() => this.reload$.next())
		);
	}


	getStudentCourses(classroomId: number | string, page: number = 1, limit: number = 10, sort: number = 10,
		order: 'asc' | 'desc' = 'desc', statusFilter: 'all' | 'Draft' | 'Published' = 'all'): Observable<CourseResponse> {
		const params = new HttpParams()
			.set('classroom_id', classroomId.toString())
			.set('page', page.toString())
			.set('limit', limit.toString())
			.set('sort', sort)
			.set('order', order)
			.set('status_filter', statusFilter);
		return this.http.get<CourseResponse>(`${this.apiUrl}/getCourses`, { params });
	}

	patchPublishedCourseByStatus(classroomId: number, courseId: string): Observable<PublishCourseResponse> {
		return this.http.patch<PublishCourseResponse>(`${this.apiUrl}/courses/${classroomId}/${courseId}/status`, {})
	}

	private coursePayload$ = new BehaviorSubject<ManualCourse>({
		course_code: '',
		course_title: '',
		grade_level: '',
		credits: 0,
		internal_marks: 0,
		exam_marks: 0,
		// thumbnail: null,
		// course_document: null,
		course_description: '',
		course_duration: 0,
		total_units_count: 0,
		course_objectives: [],
		course_outcomes: [],
		units: [],
		textbooks: [],
		web_references_links: [],
		status: 'Draft'
	});


	getPayload(): ManualCourse {
		return this.coursePayload$.value;
	}

	payloadChanges(): Observable<ManualCourse> {
		return this.coursePayload$.asObservable();
	}

	updatePayload(partial: Partial<ManualCourse>) {
		const updated = { ...this.coursePayload$.value, ...partial };
		this.coursePayload$.next(updated);
	}

	saveCourse(
		payload: ManualCourse,
		classroomId: number = environment.classroomId
	): Observable<any> {

		const params = new HttpParams()
			.set('classroom_id', classroomId.toString());

		const formData = new FormData();

		if (payload.course_thumbnail instanceof File) {
			formData.append('course_thumbnail', payload.course_thumbnail);
		}

		if (payload.course_document instanceof File) {
			formData.append('course_document', payload.course_document);
		}

		const cleanPayload = {
			...payload,
			course_thumbnail: undefined,
			course_document: undefined
		};

		formData.append('request', JSON.stringify(cleanPayload));

		return this.http.post<any>(
			`${this.apiUrl}/createCourse`,
			formData,
			{ params }
		);
	}

	clearPayload() {
		this.coursePayload$.next({
			course_code: '',
			course_title: '',
			grade_level: '',
			credits: 0,
			internal_marks: 0,
			exam_marks: 0,
			course_description: '',
			course_duration: 0,
			total_units_count: 0,
			course_objectives: [],
			course_outcomes: [],
			units: [],
			textbooks: [],
			web_references_links: [],
			status: 'Draft',

		});
	}
	getManualCoursesByCourseId(courseId: string | number): Observable<CompleteSyllabusResponseManual> {
		return this.http.get<CompleteSyllabusResponseManual>(`${this.apiUrl}/getCourseByCourseId/${courseId}`);
	}
}
